/**
 * Club-configurable main drawer visibility (Member + Coach audiences).
 * Role gates in NavBar still apply; these toggles only hide/show within that audience.
 */

export const NAV_MENU_AUDIENCES = Object.freeze(["member", "coach"]);

/**
 * Configurable drawer items for Club Settings → Menu.
 * `locked`: always on, not shown as a toggle (or toggle disabled).
 * `audiences`: which columns can control this item.
 * `requiresCoach`: still needs isCoach (or admin for availability).
 * `requiresStaff`: needs isAdmin || isCoach.
 */
export const NAV_MENU_CONFIG_ITEMS = Object.freeze([
  {
    id: "home",
    page: "LandingPage",
    labelKey: "home_page",
    audiences: ["member", "coach"],
    locked: true,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "profile",
    page: "ProfilePage",
    labelKey: "my_profile",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "belts",
    page: "ProfileBeltsPage",
    labelKey: "belt",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "messages",
    page: "MessagePage",
    labelKey: "messages",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "news",
    page: "NewsPage",
    labelKey: "news",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "training_program",
    page: "TrainingProgramPage",
    labelKey: "training_program",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "video_library",
    page: "VideoLibraryPage",
    labelKey: "video_library_title",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "pos_shop",
    page: "PosShopPage",
    labelKey: "pos_shop_title",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "schedule",
    page: "SchedulePage",
    labelKey: "schedule_title",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "digital_card",
    page: "DigitalCardPage",
    labelKey: "membership_card",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "documentation",
    page: "DocumentationPage",
    labelKey: "documentation_title",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
  },
  {
    id: "settings",
    page: "SettingsPage",
    labelKey: "admin_settings",
    audiences: ["coach"],
    locked: false,
    defaultMember: false,
    defaultCoach: true,
    requiresStaff: true,
  },
  {
    id: "instructor_schedule",
    page: "InstructorSchedulePage",
    labelKey: "instructors_agenda",
    audiences: ["coach"],
    locked: false,
    defaultMember: false,
    defaultCoach: true,
    requiresCoach: true,
  },
  {
    id: "instructor_availability",
    page: "InstructorAvailabilityPage",
    labelKey: "instructor_availability_title",
    audiences: ["member", "coach"],
    locked: false,
    defaultMember: true,
    defaultCoach: true,
    requiresCoachOrAdmin: true,
  },
]);

const PAGE_TO_ITEM = Object.freeze(
  Object.fromEntries(NAV_MENU_CONFIG_ITEMS.map((item) => [item.page, item]))
);

export function getNavMenuItemByPage(page) {
  return PAGE_TO_ITEM[page] || null;
}

export function defaultNavMenuVisibility() {
  const member = {};
  const coach = {};
  for (const item of NAV_MENU_CONFIG_ITEMS) {
    if (item.audiences.includes("member")) member[item.id] = Boolean(item.defaultMember);
    if (item.audiences.includes("coach")) coach[item.id] = Boolean(item.defaultCoach);
  }
  return { member, coach };
}

function coerceAudienceMap(raw, audience, defaults) {
  const base = { ...(defaults[audience] || {}) };
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return base;
  const out = { ...base };
  for (const item of NAV_MENU_CONFIG_ITEMS) {
    if (!item.audiences.includes(audience)) continue;
    if (item.locked) {
      out[item.id] = true;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(raw, item.id)) {
      out[item.id] = Boolean(raw[item.id]);
    }
  }
  return out;
}

/** Normalize API / DB payload into a full { member, coach } map with defaults. */
export function normalizeNavMenuVisibility(raw) {
  const defaults = defaultNavMenuVisibility();
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return defaults;
  return {
    member: coerceAudienceMap(raw.member, "member", defaults),
    coach: coerceAudienceMap(raw.coach, "coach", defaults),
  };
}

/** Compact payload for PUT (omit locked keys). */
export function serializeNavMenuVisibility(config) {
  const normalized = normalizeNavMenuVisibility(config);
  const pick = (audience) => {
    const out = {};
    for (const item of NAV_MENU_CONFIG_ITEMS) {
      if (!item.audiences.includes(audience) || item.locked) continue;
      out[item.id] = Boolean(normalized[audience]?.[item.id]);
    }
    return out;
  };
  return { member: pick("member"), coach: pick("coach") };
}

export function resolveNavMenuAudience(user) {
  if (!user) return "member";
  if (user.isCoach === true || user.isAdmin === true) return "coach";
  return "member";
}

export function isNavMenuItemEnabled(config, audience, itemId) {
  const normalized = normalizeNavMenuVisibility(config);
  const item = NAV_MENU_CONFIG_ITEMS.find((i) => i.id === itemId);
  if (!item) return true;
  if (item.locked) return true;
  if (!item.audiences.includes(audience)) {
    // Member audience looking at coach-only item → hidden
    return false;
  }
  return Boolean(normalized[audience]?.[itemId]);
}

/** Page-level helper for NavBar. */
export function isNavMenuPageEnabled(config, user, page, extras = {}) {
  const item = getNavMenuItemByPage(page);
  if (!item) return true; // non-configurable pages (board, color promotion, video library admin, …)
  let audience = resolveNavMenuAudience(user);
  if (
    page === "InstructorAvailabilityPage" &&
    extras.inInstructorsGroup === true &&
    user?.isCoach !== true &&
    user?.isAdmin !== true
  ) {
    audience = "member";
  }
  return isNavMenuItemEnabled(config, audience, item.id);
}

export const NAV_MENU_VISIBILITY_UPDATED_EVENT = "navMenuVisibilityUpdated";
